// src/components/Locale/PreferredLocaleModal.tsx
// One-shot : si preferredLocale absent → l’utilisateur choisit FR/EN (app + push).

import React, { useEffect, useState } from 'react';
import { Modal, Radio, Button, Typography, Space } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import type { AppLocale } from '../../i18n';
import { isAppLocale } from '../../i18n';
import { APP_LOCALES } from '../../i18n/types';

const { Title, Paragraph } = Typography;

const BRAND = '#E91E63';

const PreferredLocaleModal: React.FC = () => {
  const { t } = useAppTranslation('common');
  const { user, userProfile, patchUserProfile } = useAuth();
  const { locale, setLocale } = useLocale();
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState<AppLocale>(locale);
  const [saving, setSaving] = useState(false);

  const needsChoice =
    !!user?.uid && !!userProfile && !isAppLocale(userProfile.preferredLocale);

  useEffect(() => {
    if (!needsChoice) {
      setOpen(false);
      return;
    }
    setChoice(isAppLocale(locale) ? locale : 'fr');
    const timer = window.setTimeout(() => setOpen(true), 600);
    return () => window.clearTimeout(timer);
  }, [needsChoice, locale]);

  const handleConfirm = async () => {
    if (!user?.uid || !isAppLocale(choice)) return;
    setSaving(true);
    try {
      setLocale(choice);
      await patchUserProfile({ preferredLocale: choice });
      setOpen(false);
    } catch (error) {
      console.warn('Impossible d’enregistrer preferredLocale:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!needsChoice) return null;

  return (
    <Modal
      open={open}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      centered
      width={420}
      styles={{
        body: { padding: '28px 24px 20px' },
      }}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div style={{ textAlign: 'center' }}>
          <GlobalOutlined style={{ fontSize: 36, color: BRAND }} />
          <Title level={4} style={{ marginTop: 12, marginBottom: 8 }}>
            {t('localeGate.title')}
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('localeGate.lead')}
          </Paragraph>
        </div>

        <Radio.Group
          value={choice}
          onChange={(e) => setChoice(e.target.value as AppLocale)}
          optionType="button"
          buttonStyle="solid"
          style={{ display: 'flex', width: '100%' }}
        >
          {APP_LOCALES.map((lng) => (
            <Radio.Button
              key={lng}
              value={lng}
              style={{ flex: 1, textAlign: 'center', height: 44, lineHeight: '44px' }}
            >
              {t(`language.${lng}`)}
            </Radio.Button>
          ))}
        </Radio.Group>

        <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 0 }}>
          {t('localeGate.hint')}
        </Paragraph>

        <Button
          type="primary"
          block
          size="large"
          loading={saving}
          onClick={handleConfirm}
          style={{ background: BRAND, borderColor: BRAND, fontWeight: 600 }}
        >
          {t('localeGate.confirm')}
        </Button>
      </Space>
    </Modal>
  );
};

export default PreferredLocaleModal;
